import React, { Component } from 'react';
import { connect } from 'react-redux';
import { submit, initialize, destroy } from 'redux-form';
import moment from 'moment';
import { bool, shape, string, any, number } from 'prop-types';
import { push } from 'connected-react-router';
import { evaluateCaptcha } from '@widergy/web-utils/lib/form';
import { lengthEquals, validDate, maxDate } from '@widergy/web-utils/lib/validate';
import { mapUserFormValues } from '@widergy/utilitygo-forms-web';
import { AlertHandler } from '@widergy/energy-ui';

import { ROUTES, DEFAULT_ROUTES } from 'constants/routes';
import { actionCreators as authActions, currentUserPropTypes } from 'redux/authHandlers';
import { appConfig } from 'config/customization';
import { actionCreators as accountActions, accountPropTypes } from 'redux/accountHandlers';
import { DIGITAL_BILL_FORM } from 'constants/general';
import { message } from 'utils/stringUtils';
import {
  docTypeOptions,
  documentTypeField,
  documentNumberField,
  nameField,
  lastNameField,
  contactFirstNameField,
  contactLastNameField,
  genderField,
  bornDateField,
  carrierField,
  mobileField,
  phoneField,
  platformDiscoveryField
} from 'constants/fields';
import { SIGNUP_PERSONAL_INFO, SIGNUP_USER_REGISTER } from 'constants/reduxForms';
import { trackEvent, gaTrackPageView } from 'services/AnalyticsService';
import { categories, actions, labels } from 'constants/analytics';

import { steps, isActive } from './constants';
import SignUp from './layout';

const isCurrentStep = currentStep => element => element.stepNumber === currentStep;

const fieldsConfig = appConfig.signUp.personalInformationFields;

const valueMapper = values => ({
  date_of_birth: values.bornDate && moment(values.bornDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
  mobile: values.cellNumber && {
    number: values.cellNumber,
    carrier: values.carrier,
    default: true
  },
  document_type:
    values.document_type && docTypeOptions.find(element => element.value === values.document_type).key,
  document_number: values.document_number,
  last_name: values.last_name,
  first_name: values.name,
  contact_first_name: values.contact_first_name,
  contact_last_name: values.contact_last_name,
  gender: values.gender,
  phone: values.phone && {
    number: values.phone
  },
  platform_discovery_channel: values.platform_discovery
});

const existsWrongAnswer = (questions, values) =>
  questions.questions.some(
    question => values[question.key] !== question.field_options.find(answer => answer.correct).name
  );

const retrieveInitialFields = () =>
  appConfig.signUp.retrieveUserIdentification
    ? [documentTypeField, documentNumberField, genderField]
    : [
        documentTypeField,
        documentNumberField,
        nameField,
        lastNameField,
        contactFirstNameField,
        contactLastNameField,
        genderField,
        bornDateField,
        carrierField,
        mobileField,
        phoneField,
        platformDiscoveryField
      ].filter(field => fieldsConfig[field.key].show);

const addReadOnly = field => ({ ...field, read_only: true });

class SignUpContainer extends Component {
  state = { fields: retrieveInitialFields() };

  componentDidMount() {
    if (this.requiresRedirect()) {
      return this.props.dispatch(push(DEFAULT_ROUTES.PRIVATE));
    }
    return gaTrackPageView(ROUTES.SIGN_UP);
  }

  componentDidUpdate = prevProps => {
    if (this.requiresRedirect()) {
      return this.props.dispatch(push(DEFAULT_ROUTES.PRIVATE));
    }
    if (
      prevProps.userIdentificationLoading &&
      !this.props.userIdentificationLoading &&
      this.props.userIdentification
    ) {
      this.handleUpdateFields(this.props.userIdentification);
    }
    return null;
  };

  componentWillUnmount = () => {
    this.props.dispatch(authActions.resetSignUpInfo());
  };

  requiresRedirect = () => !this.props.loading && this.props.currentStep === 1 && this.props.currentUser;

  resetFields = () => {
    this.props.dispatch(authActions.signUpClearUserIdentification());
    this.setState({ fields: retrieveInitialFields() });
  };

  handleSignUp = values => {
    this.props.dispatch(authActions.updateSignUpUserInformation(values));
    // Two lines below are done because captcha sync error doesn't refresh properly with
    // change & untouch actions creators
    // For more info look at https://github.com/erikras/redux-form/issues/2971
    this.props.dispatch(destroy(SIGNUP_USER_REGISTER));
    this.props.dispatch(initialize(SIGNUP_USER_REGISTER, { email: values.email, password: values.password }));
    if (this.props.nextStepOnSuccess === 4) {
      this.props.dispatch(authActions.updateSignUpStep());
    } else {
      this.props.dispatch(authActions.signUp());
    }
  };

  handleResendCode = (email, documentType, documentNumber) => {
    this.props.dispatch(authActions.resendVerificationCode(email, documentType, documentNumber));
  };

  handleVerifyEmail = values => {
    this.props.dispatch(authActions.validateEmail(values.code));
  };

  handleAddAccount = values => {
    this.props.dispatch(authActions.signUpSetInitialAccount(values.clientNumber));
    if (this.props.nextStepOnSuccess !== 0) {
      this.props.dispatch(authActions.updateSignUpStep());
    } else {
      this.props.dispatch(accountActions.addAccount(values.clientNumber));
    }
  };

  handleSendUserInfo = async values => {
    const isValid = await evaluateCaptcha(values, this.state.fields);
    if (isValid) {
      if (
        appConfig.signUp.retrieveUserIdentification &&
        (!this.props.userIdentification || this.props.userIdentificationError)
      ) {
        this.props.dispatch(authActions.getUserIdentification(mapUserFormValues(this.state.fields, values)));
      } else {
        this.props.dispatch(
          authActions.updateSignUpUserInformation(valueMapper(mapUserFormValues(this.state.fields, values)))
        );
        this.props.dispatch(authActions.updateSignUpStep());
      }
    }
  };

  handleUpdateFields = userIdentification => {
    const fieldsToAdd = [];
    if (userIdentification.request_name) {
      if (!userIdentification.normal_person) {
        fieldsToAdd.push(contactFirstNameField, contactLastNameField);
      } else {
        fieldsToAdd.push(nameField, lastNameField);
      }
    }
    if (userIdentification.request_date_of_birth) {
      const bornDate = bornDateField;
      if (!userIdentification.normal_person) {
        bornDate.title = message('Signup:activitiesInitialDateTitle');
        bornDate.placeholder = message('Signup:activitiesInitialDate');
        bornDate.validate = [
          lengthEquals(10, message('Validations:invalidFormat')),
          validDate(message('Validations:invalidFormat')),
          maxDate(moment().format('DD-MM-YYYY'), message('Validations:dateIsAfterActualDate'))
        ];
      }
      fieldsToAdd.push(bornDate);
    }
    if (userIdentification.request_phone_data) fieldsToAdd.push(carrierField, mobileField, phoneField);
    if (fieldsToAdd.length > 0) {
      fieldsToAdd.filter(field => fieldsConfig[field.key].show);
      this.setState(prevState => ({
        fields: [...prevState.fields.map(element => addReadOnly(element)), ...fieldsToAdd]
      }));
    } else {
      this.props.dispatch(submit(SIGNUP_PERSONAL_INFO));
    }
  };

  handleSendIdentityAnswers = (values, _, payload) => {
    this.props.dispatch(authActions.validateIdentityAnswers(values, payload.userInformation));
  };

  handleCheckAnswers = (values, _, payload) => {
    if (existsWrongAnswer(payload, values)) {
      AlertHandler.error(message('Signup:wrongAnswers'));
      this.props.dispatch(authActions.restartSignUp(6));
      trackEvent(
        categories.REGISTRACIONES,
        actions.PREGUNTAS_RELACION_CUENTA_ERROR,
        labels.PREGUNTAS_RELACION_CUENTA_NOT_OK
      );
    } else {
      trackEvent(
        categories.REGISTRACIONES,
        actions.PREGUNTAS_RELACION_CUENTA,
        labels.PREGUNTAS_RELACION_CUENTA_OK
      );
      this.props.dispatch(accountActions.addAccount(this.props.initialAccount));
    }
  };

  handleSendCheckedAccounts = values =>
    this.props.dispatch(authActions.signUpSetRelatedAccounts(values.accountList || []));

  handleAcceptServiceAgreement = values => {
    const firstAccountServiceAgreement = this.props.currentStep === 8;
    if (values.accountList && values.accountList.length > 0) {
      this.props.dispatch(
        accountActions.acceptServiceAgreement(
          DIGITAL_BILL_FORM,
          values.accountList,
          firstAccountServiceAgreement
        )
      );
    } else if (this.props.nextStepOnSuccess !== 0) {
      if (firstAccountServiceAgreement) {
        this.props.dispatch(authActions.updateSignUpStep());
      } else {
        this.props.dispatch(push(ROUTES.ACCOUNT_STATUS));
      }
      trackEvent(
        categories.FACTURACION,
        actions.NO_ADHESION_FACTURA_DIGITAL,
        labels.NO_ADHESION_FACTURA_DIGITAL_OK
      );
    } else {
      trackEvent(
        categories.FACTURACION,
        actions.NO_ADHESION_FACTURA_DIGITAL,
        labels.NO_ADHESION_FACTURA_DIGITAL_OK
      );
      this.props.dispatch(push(ROUTES.ACCOUNT_STATUS));
    }
  };

  handleAcceptTerms = () => this.props.dispatch(authActions.signUp());

  childProps = () => {
    switch (this.props.currentStep) {
      case 1: {
        return {
          fields: this.state.fields,
          onSubmit: this.handleSendUserInfo,
          resetFields: this.resetFields,
          loading: this.props.loading
        };
      }
      case 2: {
        return {
          onSubmit: this.handleSendIdentityAnswers
        };
      }
      case 3: {
        return {
          onSubmit: this.handleSignUp,
          loading: this.props.loading
        };
      }
      case 4: {
        return {
          onSubmit: this.handleAcceptTerms
        };
      }
      case 5: {
        return {
          onSubmit: this.handleVerifyEmail,
          onResendCode: this.handleResendCode,
          loading: this.props.loading
        };
      }
      case 6: {
        return {
          onSubmit: this.handleAddAccount
        };
      }
      case 7: {
        return {
          onSubmit: this.handleCheckAnswers
        };
      }
      case 8: {
        return {
          onSubmit: this.handleAcceptServiceAgreement,
          stepNumber: 8,
          initialAccount: this.props.initialAccount
        };
      }
      case 9: {
        return {
          onSubmit: this.handleSendCheckedAccounts
        };
      }
      case 10: {
        return {
          onSubmit: this.handleAcceptServiceAgreement,
          stepNumber: 10,
          initialAccount: this.props.initialAccount
        };
      }
      default: {
        return {};
      }
    }
  };

  render() {
    const ActiveSteps = steps.filter(isActive);
    const StepComponent = steps.find(isCurrentStep(this.props.currentStep)).component;
    const stepNumber = ActiveSteps.indexOf(ActiveSteps.find(isCurrentStep(this.props.location.pathname)));
    return (
      <SignUp currentStep={stepNumber} activeSteps={ActiveSteps}>
        <StepComponent {...this.childProps()} {...this.props.location.state} />
      </SignUp>
    );
  }
}

SignUpContainer.propTypes = {
  location: any, // eslint-disable-line react/forbid-prop-types
  currentUser: currentUserPropTypes,
  currentAccount: accountPropTypes,
  loading: bool,
  userIdentification: shape({
    first_name: string,
    last_name: string,
    maximum_date_of_birth: string,
    minimum_date_of_birth: string,
    normal_person: bool,
    request_date_of_birth: bool,
    request_name: bool,
    request_phone_data: bool
  }),
  userIdentificationLoading: bool,
  userIdentificationError: string,
  initialAccount: string,
  currentStep: number,
  nextStepOnSuccess: number
};

const mapStateToProps = store => ({
  loading:
    store.auth.sessionLoading ||
    store.auth.userInformationLoading ||
    store.auth.signUpLoading ||
    store.auth.validateEmailLoading ||
    store.auth.loadingVerificationCode,
  currentUser: store.auth.currentUser,
  currentAccount: store.account.currentAccount,
  userIdentification: store.auth.userIdentification,
  userIdentificationLoading: store.auth.userIdentificationLoading,
  userIdentificationError: store.auth.userIdentificationError,
  currentStep: store.auth.signUpFlow.currentStep,
  nextStepOnSuccess: store.auth.signUpFlow.nextStepOnSuccess,
  initialAccount: store.auth.signUpFlow.initialAccount
});

export default connect(mapStateToProps)(SignUpContainer);
